import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FiSearch, FiX, FiChevronDown, FiCheckSquare, FiHelpCircle, FiRefreshCw } from 'react-icons/fi';

/**
 * BIMultiSelectSearch
 * Componente unificado de multi-seleção com busca no padrão visual dos filtros de BI.
 * 
 * Suporta:
 * - value como Array (ex: ['1', '2']) ou String separada por vírgula (ex: '1,2')
 * - options estático ou fetchData assíncrono (com paginação e busca debounce)
 * - showCodePrefix, formatLabel, getOptionLabel, getOptionValue, onOptionSelected
 */
export default function BIMultiSelectSearch({
  label,
  placeholder = 'Buscar...',
  value,
  onChange,
  options = null,
  fetchData = null,
  badgeColor = '#3b82f6',
  showCodePrefix = false,
  formatLabel = null,
  getOptionLabel = null,
  getOptionValue = null,
  onOptionSelected = null,
  required = false,
  infiniteScroll = false,
  style = {},
  getOptionStyle = null,
  getOptionClassName = null,
  renderOptionBadge = null,
  loading: externalLoading = false,
  loadingPlaceholder = null,
  disabled = false
}) {
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [asyncOptions, setAsyncOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);

  const isLoading = Boolean(externalLoading || loading);

  // Detectar formato do valor (Array vs String com vírgula)
  const isStringValue = typeof value === 'string';

  const selectedValues = useMemo(() => {
    if (!value && value !== 0) return [];
    if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
    return String(value)
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  }, [value]);

  const selectedCount = selectedValues.length;

  // Fechar ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fechar no Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Foco no campo de busca ao abrir
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  const extractItemsFromResponse = (response) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    const target = response.data !== undefined ? response.data : response;
    if (Array.isArray(target)) return target;
    if (typeof target === 'object' && target !== null) {
      for (const key of Object.keys(target)) {
        if (Array.isArray(target[key])) {
          return target[key];
        }
      }
    }
    return [];
  };

  // Carregar opções assíncronas se fetchData estiver presente
  const loadAsyncOptions = useCallback(
    async (term = '') => {
      if (!fetchData) return;
      try {
        setLoading(true);
        const limit = infiniteScroll ? 200 : 1000;
        const response = await fetchData(term, limit, 0);
        const items = extractItemsFromResponse(response);
        setAsyncOptions(items);
      } catch (err) {
        console.error('Erro ao carregar opções assíncronas:', err);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, infiniteScroll]
  );

  useEffect(() => {
    if (fetchData && isOpen && !hasLoadedInitial) {
      loadAsyncOptions('');
      setHasLoadedInitial(true);
    }
  }, [fetchData, isOpen, hasLoadedInitial, loadAsyncOptions]);

  // Busca debounced para fetchData
  useEffect(() => {
    if (!fetchData || !isOpen || !hasLoadedInitial) return;
    const timer = setTimeout(() => {
      loadAsyncOptions(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchData, isOpen, hasLoadedInitial, loadAsyncOptions]);

  // Carregar dados dos itens já selecionados para exibição de label correto
  useEffect(() => {
    if (!fetchData || selectedValues.length === 0 || hasLoadedInitial) return;
    loadAsyncOptions('');
    setHasLoadedInitial(true);
  }, [fetchData, selectedValues, hasLoadedInitial, loadAsyncOptions]);

  // Lista de opções ativa (estática ou assíncrona)
  const rawOptions = options !== null ? options : asyncOptions;

  // Extrair valor e label de um item de forma robusta
  const extractVal = useCallback(
    (opt) => {
      if (!opt && opt !== 0) return '';
      if (getOptionValue) return String(getOptionValue(opt));
      if (opt.codigo !== undefined && opt.codigo !== null) return String(opt.codigo);
      if (opt.codprod !== undefined && opt.codprod !== null) return String(opt.codprod);
      if (opt.codfunc !== undefined && opt.codfunc !== null) return String(opt.codfunc);
      if (opt.codativi !== undefined && opt.codativi !== null) return String(opt.codativi);
      if (opt.codmarca !== undefined && opt.codmarca !== null) return String(opt.codmarca);
      if (opt.codcategoria !== undefined && opt.codcategoria !== null) return String(opt.codcategoria);
      if (opt.id !== undefined && opt.id !== null) return String(opt.id);
      if (opt.code !== undefined && opt.code !== null) return String(opt.code);
      return String(opt);
    },
    [getOptionValue]
  );

  const extractLabel = useCallback(
    (opt) => {
      if (!opt && opt !== 0) return '';
      if (formatLabel) return formatLabel(opt);
      if (getOptionLabel) return getOptionLabel(opt);
      const name =
        opt.label ||
        opt.nomefantasia ||
        opt.nome ||
        opt.razaosocial ||
        opt.descricao ||
        opt.ramo ||
        opt.categoria ||
        opt.marca ||
        opt.name ||
        '';
      const code =
        opt.codigo !== undefined
          ? opt.codigo
          : opt.codprod !== undefined
          ? opt.codprod
          : opt.codfunc !== undefined
          ? opt.codfunc
          : opt.codativi !== undefined
          ? opt.codativi
          : opt.codmarca !== undefined
          ? opt.codmarca
          : opt.codcategoria !== undefined
          ? opt.codcategoria
          : opt.id !== undefined
          ? opt.id
          : '';

      if (showCodePrefix && code) {
        return `#${code} - ${name || `Cód. ${code}`}`;
      }
      return name || (code ? `Cód. ${code}` : String(opt));
    },
    [formatLabel, getOptionLabel, showCodePrefix]
  );

  // Filtragem local das opções
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return rawOptions;
    const q = searchTerm.toLowerCase().trim();
    return rawOptions.filter((opt) => {
      const labelStr = extractLabel(opt).toLowerCase();
      const valStr = extractVal(opt).toLowerCase();
      const rawName = String(
        opt.ramo || opt.categoria || opt.marca || opt.descricao || opt.nome || opt.nomefantasia || opt.label || ''
      ).toLowerCase();
      return labelStr.includes(q) || valStr.includes(q) || rawName.includes(q);
    });
  }, [rawOptions, searchTerm, extractLabel, extractVal]);

  // Disparar onChange mantendo o tipo original
  const emitChange = useCallback(
    (newArr) => {
      if (isStringValue) {
        onChange(newArr.join(','));
      } else {
        onChange(newArr);
      }
    },
    [isStringValue, onChange]
  );

  // Alternar seleção de um item individual
  const handleToggle = (opt) => {
    const val = extractVal(opt);
    let updated;
    if (selectedValues.includes(val)) {
      updated = selectedValues.filter((v) => v !== val);
    } else {
      updated = [...selectedValues, val];
    }
    emitChange(updated);
    if (onOptionSelected) {
      onOptionSelected(opt);
    }
  };

  // Selecionar todos / desmarcar todos os filtrados
  const allFilteredSelected = useMemo(() => {
    if (filteredOptions.length === 0) return false;
    return filteredOptions.every((opt) => selectedValues.includes(extractVal(opt)));
  }, [filteredOptions, selectedValues, extractVal]);

  const someFilteredSelected = useMemo(() => {
    return filteredOptions.some((opt) => selectedValues.includes(extractVal(opt)));
  }, [filteredOptions, selectedValues, extractVal]);

  const handleToggleSelectAll = () => {
    const filteredVals = filteredOptions.map(extractVal);
    if (allFilteredSelected) {
      const filteredSet = new Set(filteredVals);
      emitChange(selectedValues.filter((v) => !filteredSet.has(v)));
    } else {
      const newSet = new Set([...selectedValues, ...filteredVals]);
      emitChange(Array.from(newSet));
    }
  };

  // Limpar todas as seleções
  const handleClearAll = (e) => {
    if (e) e.stopPropagation();
    emitChange([]);
    setSearchTerm('');
  };

  // Rótulo de exibição resumido para o input trigger
  const displayTriggerText = useMemo(() => {
    if (selectedCount === 0) return '';
    if (selectedCount === 1) {
      const singleVal = selectedValues[0];
      const match = rawOptions.find((o) => extractVal(o) === singleVal);
      return match ? extractLabel(match) : `Item #${singleVal}`;
    }
    // Para múltiplos itens selecionados
    const firstTwo = selectedValues
      .slice(0, 2)
      .map((val) => {
        const match = rawOptions.find((o) => extractVal(o) === val);
        return match ? extractLabel(match) : `#${val}`;
      })
      .join(', ');
    const extraCount = selectedCount - 2;
    return extraCount > 0
      ? `${selectedCount} selecionados (${firstTwo} +${extraCount})`
      : `${selectedCount} selecionados: ${firstTwo}`;
  }, [selectedCount, selectedValues, rawOptions, extractVal, extractLabel]);

  return (
    <div
      className="form-group-modern mb-0"
      style={{
        position: 'relative',
        width: '100%',
        minWidth: 0,
        zIndex: isOpen ? 1060 : 'auto',
        ...style
      }}
      ref={containerRef}
    >
      {label && (
        <label className="d-flex justify-content-between align-items-center mb-1">
          <span className="d-inline-flex align-items-center gap-1">
            {label} {required && <span className="text-danger">*</span>}
            {isLoading && (
              <span className="text-muted ms-1 d-inline-flex align-items-center gap-1" style={{ fontSize: '0.72rem', fontWeight: 'normal' }}>
                <FiRefreshCw size={10} className="spin-ico text-primary" />
                <span className="text-primary" style={{ fontSize: '0.7rem' }}>Carregando...</span>
              </span>
            )}
          </span>
          {selectedCount > 0 && (
            <span
              className="text-muted"
              style={{ fontSize: '0.72rem', cursor: 'pointer' }}
              onClick={handleClearAll}
              title="Limpar seleções"
            >
              Limpar ({selectedCount})
            </span>
          )}
        </label>
      )}

      <div
        className={`bi-search-dropdown-container ${isOpen ? 'is-open' : ''} ${isLoading ? 'is-loading' : ''}`}
        style={{ position: 'relative', zIndex: isOpen ? 1060 : 'auto' }}
      >
        <div className="bi-search-input-wrapper">
          <input
            type="text"
            readOnly
            disabled={disabled}
            className={`form-control-modern bi-search-input-box ${selectedCount > 0 ? 'is-selected' : ''}`}
            placeholder={isLoading ? (loadingPlaceholder || placeholder || 'Carregando...') : placeholder}
            value={displayTriggerText}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            style={disabled ? { cursor: 'not-allowed', opacity: 0.7 } : { cursor: 'pointer' }}
          />

          <div className="bi-search-actions-inside">
            {isLoading && (
              <span
                className="bi-search-loading-icon d-inline-flex align-items-center"
                style={{ marginRight: '4px' }}
                title={loadingPlaceholder || "Carregando opções..."}
              >
                <FiRefreshCw size={13} className="spin-ico text-primary" />
              </span>
            )}
            {selectedCount > 0 && (
              <span
                className="bi-selected-badge"
                style={{
                  backgroundColor: `${badgeColor}15`,
                  borderColor: `${badgeColor}40`,
                  color: badgeColor
                }}
                title={`${selectedCount} item(s) selecionado(s)`}
              >
                <FiCheckSquare size={12} /> {selectedCount}
              </span>
            )}
            {selectedCount > 0 && (
              <span
                className="bi-search-clear-icon"
                title="Limpar seleção"
                onClick={handleClearAll}
              >
                <FiX size={15} />
              </span>
            )}
            <span
              className={`bi-search-chevron ${isOpen ? 'open' : ''}`}
              onClick={() => !disabled && setIsOpen(!isOpen)}
              title={isOpen ? 'Fechar lista' : 'Abrir lista'}
              style={disabled ? { cursor: 'not-allowed' } : { cursor: 'pointer' }}
            >
              <FiChevronDown size={16} />
            </span>
          </div>
        </div>

        {/* Modal Suspenso (Popover) */}
        {isOpen && (
          <div className="bi-search-popover">
            {/* Barra de Busca Interna */}
            <div className="bi-popover-search-bar">
              <FiSearch size={14} className="bi-popover-search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                className="bi-popover-search-input"
                placeholder={placeholder || 'Buscar opções...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setIsOpen(false);
                }}
              />
            </div>

            {/* Cabeçalho do Popover com Selecionar Todos */}
            <div className="bi-popover-header">
              <label className="bi-popover-select-all">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate = someFilteredSelected && !allFilteredSelected;
                    }
                  }}
                  onChange={handleToggleSelectAll}
                />
                <span>Selecionar todos ({filteredOptions.length})</span>
              </label>

              <div className="bi-popover-header-actions">
                {selectedCount > 0 && (
                  <button
                    type="button"
                    className="bi-popover-clear-btn"
                    onClick={handleClearAll}
                  >
                    Desmarcar Todos
                  </button>
                )}
                <span className="bi-popover-count-tag">
                  {selectedCount} / {rawOptions.length}
                </span>
              </div>
            </div>

            {/* Lista de Opções */}
            <div className="bi-popover-list">
              {isLoading ? (
                <div className="bi-popover-empty">
                  <FiRefreshCw size={18} className="spin-ico d-block mx-auto mb-2 text-primary" />
                  {loadingPlaceholder || 'Carregando opções...'}
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="bi-popover-empty">
                  <FiHelpCircle size={22} className="d-block mx-auto mb-1 opacity-50" />
                  Nenhum resultado encontrado{searchTerm ? ` para "${searchTerm}"` : ''}.
                </div>
              ) : (
                filteredOptions.map((opt, idx) => {
                  const val = extractVal(opt);
                  const isSelected = selectedValues.includes(val);
                  const labelText = extractLabel(opt);
                  const optStyle = getOptionStyle ? getOptionStyle(opt) : {};
                  const optClass = getOptionClassName ? getOptionClassName(opt) : '';
                  const optBadge = renderOptionBadge ? renderOptionBadge(opt) : null;
                  const initials = (labelText || 'OP')
                    .replace(/[^a-zA-Z0-9]/g, '')
                    .substring(0, 2)
                    .toUpperCase();

                  return (
                    <div
                      key={`${val}-${idx}`}
                      className={`bi-popover-item ${isSelected ? 'selected' : ''} ${optClass}`}
                      style={optStyle}
                      onClick={() => handleToggle(opt)}
                    >
                      <div className="bi-popover-item-left">
                        <input
                          type="checkbox"
                          className="bi-popover-checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div
                          className="bi-popover-avatar"
                          style={{
                            background: isSelected
                              ? `linear-gradient(135deg, ${badgeColor}, #1d4ed8)`
                              : '#e2e8f0',
                            color: isSelected ? '#ffffff' : '#64748b'
                          }}
                        >
                          {initials}
                        </div>
                        <div className="bi-popover-info">
                          <span className="bi-popover-name" title={labelText}>
                            {labelText}
                          </span>
                          {(opt.codigo || opt.codfilial || opt.id) && (
                            <div className="bi-popover-meta">
                              {opt.codigo ? <span>Cód: #{opt.codigo}</span> : (opt.id ? <span>ID: #{opt.id}</span> : null)}
                              {opt.codfilial && <span>• Filial {opt.codfilial}</span>}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="bi-popover-item-right">
                        {optBadge}
                        {isSelected ? (
                          <span
                            className="d-flex align-items-center gap-1 fw-bold"
                            style={{ fontSize: '0.74rem', color: '#16a34a' }}
                          >
                            <FiCheckSquare size={14} /> Ativo
                          </span>
                        ) : (
                          val && <span className="bi-popover-target-val">#{val}</span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Rodapé do Popover */}
            <div className="bi-popover-footer">
              <span className="bi-popover-footer-summary">
                {selectedCount === 0
                  ? 'Nenhum selecionado'
                  : `${selectedCount} item(s) selecionado(s)`}
              </span>
              <div className="bi-popover-footer-btns">
                <button
                  type="button"
                  className="btn-modern btn-modern-primary btn-sm py-1 px-3"
                  style={{ fontSize: '0.76rem' }}
                  onClick={() => setIsOpen(false)}
                >
                  Concluir
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
